import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Gear, Plus, Minus, PaperPlaneRight, ChatText, User, Users, UsersThree,
  FileText, Folder, IdentificationBadge, X, Trash, Paperclip, ArrowLeft, Moon,
  TreeStructure, ArrowsIn, Archive, CaretDown, CaretRight, FunnelSimple, ListBullets,
  ClipboardText, Check, CheckCircle, UploadSimple, Brain, CircleNotch,
} from "@phosphor-icons/react";

const STATUS_COLORS = {
  "Concept": "#808080",
  "Research & Marketing": "#0a1f9e",
  "Workflow/Architecture/Docs.": "#4a90d9",
  "Design": "#e8712a",
  "Development": "#ffe100",
  "Team Review": "#cc55cc",
  "QA/UX/Training": "#f2cce8",
  "Production": "#22c55e",
};
const PWR = (n) => `/pwr/${n}.png`;
const IMG_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/pwr/files/`;
const imgSrc = (v) => (!v ? "" : (v.startsWith("data:") || v.startsWith("http") ? v : IMG_BASE + v));
const SRC_ICON = { "Chat Notes": "chatlogs-grey", "Task/Assignment": "task-grey", "Research & Marketing": "research-grey", "Documents": "document-grey" };
const silo = (gender, active) => PWR(`${gender === "female" ? "female" : "male"}-${active ? "green" : "black"}`);
const byOrder = (a, b) => ((Number(a.speak_order) || 999) - (Number(b.speak_order) || 999));

export default function WarRoom() {
  const { user, refresh } = useAuth();
  const humanName = user?.name || "You";
  const simonCredits = Number(user?.simon_credits ?? 0);
  const outOfCredits = simonCredits <= 0;
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ roles: [], statuses: [], source_types: [], models: [] });
  const [collaborators, setCollaborators] = useState([]);
  const [projects, setProjects] = useState([]);
  const [cards, setCards] = useState([]);
  const [activeIds, setActiveIds] = useState(new Set());
  const [humanActive, setHumanActive] = useState(true);
  const [selectProjectsMode, setSelectProjectsMode] = useState(false);
  const [activeProject, setActiveProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typingName, setTypingName] = useState(null);
  const [draft, setDraft] = useState("");
  const [composer, setComposer] = useState("chat"); // chat | task | research
  const [taskFields, setTaskFields] = useState({ mention: "", to_do: "", start_date: "", end_date: "" });
  const [saveSource, setSaveSource] = useState("Chat");
  const [subject, setSubject] = useState("");

  // modals
  const [showSettings, setShowSettings] = useState(false);
  const [showProjectPanel, setShowProjectPanel] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [profileCard, setProfileCard] = useState(null);

  const loadAll = useCallback(() => {
    api.get("/pwr/collaborators").then((r) => setCollaborators(r.data)).catch(() => {});
    api.get("/pwr/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/cards").then((r) => setCards(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/pwr/meta").then((r) => setMeta(r.data)).catch(() => {});
    loadAll();
  }, [loadAll]);

  const loadMessages = (pid) => {
    api.get(`/pwr/projects/${pid}/messages`).then((r) => setMessages(r.data)).catch(() => setMessages([]));
  };

  const toggleActive = (id) => {
    setActiveIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) {
        n.delete(id);
      } else {
        n.add(id);
        // Announce the AI joining the live session.
        const c = collaborators.find((x) => x.id === id);
        if (c && activeProject) {
          api.post("/pwr/messages", { project_id: activeProject.id, type: "chat", author: "SYSTEM", text: `${c.name || "An AI"} added to chat` })
            .then(() => loadMessages(activeProject.id)).catch(() => {});
        }
      }
      return n;
    });
  };
  const activateAll = () => setActiveIds(new Set(collaborators.map((c) => c.id)));
  const deactivateAll = () => setActiveIds(new Set());

  const fmtStamp = () =>
    new Date().toLocaleString([], { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  const postStamp = async (pid, phase) => {
    try { await api.post("/pwr/messages", { project_id: pid, type: "chat", author: "SYSTEM", text: `Session ${phase} · ${fmtStamp()}` }); }
    catch { /* non-blocking */ }
  };

  // Post a "Resuming…" recap built from the last saved session (no AI call).
  const postRecap = async (pid) => {
    try {
      const { data } = await api.get(`/pwr/projects/${pid}/recap`);
      if (!data.has_prior) return;
      const when = data.ended_at ? new Date(data.ended_at).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
      const active = (data.active_names || []).length ? (data.active_names || []).join(", ") : "none";
      let recap = (data.summary || "").replace(/^Subject:.*\n+/i, "").replace(/\s+/g, " ").trim();
      if (recap.length > 220) recap = recap.slice(0, 220) + "…";
      const text = `Resuming · last session ended ${when} · Last active: ${active}${recap ? ` · Recap: ${recap}` : ""}`;
      await api.post("/pwr/messages", { project_id: pid, type: "chat", author: "SYSTEM", text });
    } catch { /* non-blocking */ }
  };

  const selectProject = async (p) => {
    setActiveProject(p);
    setSelectProjectsMode(false);
    // Restore any saved subject for this project (survives refresh / getting kicked out)
    try { setSubject(localStorage.getItem(`simon_subject_${p.id}`) || ""); } catch { setSubject(""); }
    try {
      const { data } = await api.get(`/pwr/projects/${p.id}/messages`);
      const msgs = data || [];
      // Always post a "Session Started" stamp on selecting ANY project — EXCEPT skip if the most recent
      // message is already a "Session Started" stamp posted within the last 5 minutes (dedupe quick refreshes).
      const last = msgs[msgs.length - 1];
      const lastStartTs = last && last.author === "SYSTEM" && /^Session started/i.test(last.text || "") && last.created_at
        ? new Date(last.created_at).getTime() : 0;
      const recentStart = lastStartTs && (Date.now() - lastStartTs < 5 * 60 * 1000);
      if (!recentStart) {
        await postStamp(p.id, "started");          // date-time stamp at the START of every session
        await postRecap(p.id);                     // "Resuming…" banner from the last saved session
        await loadMessages(p.id);
      } else {
        setMessages(msgs);
      }
    } catch { setMessages([]); }
  };

  const send = async () => {
    if (!activeProject) { toast.error("Select a project first"); return; }
    if (composer === "chat" && !draft.trim()) return;
    if (composer === "chat" && outOfCredits) { toast.error("You're out of S.I.M.O.N. credits. Subscribe or top up to keep chatting."); return; }
    let payload = { project_id: activeProject.id, type: composer, author: humanActive ? humanName : "System" };
    if (composer === "chat") {
      payload.text = draft;
    } else {
      const label = composer === "task" ? "To Do" : "Research";
      payload.text = `@${taskFields.mention || "team"} Project: ${activeProject.name} | ${label}: ${taskFields.to_do} | Start: ${taskFields.start_date || "—"} | End: ${taskFields.end_date || "TBD"}`;
      payload.to_do = taskFields.to_do;
      payload.start_date = taskFields.start_date;
      payload.end_date = taskFields.end_date;
    }
    try {
      await api.post("/pwr/messages", payload);
      setDraft("");
      setTaskFields({ mention: "", to_do: "", start_date: "", end_date: "" });
      await loadMessages(activeProject.id);
      await runCascade();
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const fileInputRef = useRef(null);

  const sendImage = async (file) => {
    if (!activeProject) { toast.error("Select a project first"); return; }
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25MB)"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "attachment");
      const { data } = await api.post("/pwr/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const isImage = (data.content_type || file.type || "").startsWith("image/");
      await api.post("/pwr/messages", {
        project_id: activeProject.id, type: "chat",
        author: humanActive ? humanName : "System", text: draft.trim(),
        image: isImage ? data.path : "",
        file_path: data.path, file_name: data.name, file_type: data.content_type || file.type || "",
      });
      setDraft("");
      await loadMessages(activeProject.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) await sendImage(file);
    e.target.value = "";
  };

  const onPaste = async (e) => {
    const items = [...(e.clipboardData?.items || [])];
    const fileItem = items.find((i) => i.kind === "file");
    const pastedFile = fileItem ? fileItem.getAsFile() : (e.clipboardData?.files?.[0] || null);
    if (pastedFile) { e.preventDefault(); await sendImage(pastedFile); }
  };

  const runCascade = async () => {
    const activeList = collaborators.filter((c) => activeIds.has(c.id) && (c.name || "").trim()).slice().sort(byOrder);
    if (activeList.length === 0) return;
    for (const c of activeList) {
      setTypingName(c.name);
      try {
        await api.post(`/pwr/projects/${activeProject.id}/collab-reply`, { collaborator_id: c.id }, { timeout: 60000 });
        await loadMessages(activeProject.id);
      } catch (e) {
        toast.error(`${c.name}: ${apiError(e)}`);
      }
    }
    setTypingName(null);
  };

  const [showLog, setShowLog] = useState(false);

  const saveChat = async () => {
    if (!activeProject) return;
    if (!window.confirm("Create the AI memory file for this chat and save it to the activity log? The chat will be cleared.")) return;
    try {
      await postStamp(activeProject.id, "ended");   // date-time stamp at the END, captured in the saved document
      toast.info("Writing the AI memory file…");
      await api.post(`/pwr/projects/${activeProject.id}/save`, {
        project_id: activeProject.id, source_type: saveSource, subject: subject.trim(),
        active_collaborator_ids: Array.from(activeIds),
      }, { timeout: 90000 });
      toast.success(`Saved · AI memory file updated`);
      setMessages([]);
      setSubject("");
      try { localStorage.removeItem(`simon_subject_${activeProject.id}`); } catch { /* ignore */ }
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Persist the Subject per project so it stays on the chat across refreshes / getting kicked out.
  useEffect(() => {
    if (activeProject) {
      try { localStorage.setItem(`simon_subject_${activeProject.id}`, subject); } catch { /* ignore */ }
    }
  }, [subject, activeProject]);

  const activeCount = activeIds.size + (humanActive ? 1 : 0);

  return (
    <div className="fixed inset-0 flex flex-col bg-[#0a0a0f] text-zinc-900" style={{ fontFamily: "'IBM Plex Sans',sans-serif" }}>
      {/* BANNER */}
      <header className="h-[74px] bg-black flex items-center justify-between px-5 shrink-0 border-b border-zinc-800">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} data-testid="pwr-back" className="text-zinc-500 hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div className="bg-black rounded-md px-3 py-1.5 flex items-center shrink-0">
            <img src="/simon-logo.png" alt="S.I.M.O.N." className="h-[60px] w-auto object-contain" />
          </div>
          <p className="text-zinc-300 text-sm font-medium whitespace-nowrap">Supervised Intelligence Multi-agent Operating Network</p>
        </div>
        <button data-testid="pwr-settings-btn" onClick={() => setShowSettings(true)} className="flex flex-col items-center text-white hover:text-blue-400 transition-colors">
          <Gear size={24} weight="fill" />
          <span className="text-[10px] font-bold uppercase tracking-wide">App. Settings</span>
        </button>
      </header>

      {/* BODY */}
      <div className="flex-1 flex min-h-0">
        {/* LEFT: Collaborators */}
        <aside className={`w-60 bg-zinc-100 flex flex-col shrink-0 ${showSettings || showProjectPanel ? "opacity-60" : ""}`}>
          <div className="bg-zinc-200 px-4 py-3 border-b border-zinc-300">
            <h2 className="font-bold text-sm uppercase tracking-wide text-zinc-800">Collaborators</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {collaborators.length === 0 && (
              <p className="text-xs text-zinc-500 p-2">Add AI collaborators in App. Settings.</p>
            )}
            {collaborators.filter((c) => (c.name || "").trim()).slice().sort(byOrder).map((c) => {
              const active = activeIds.has(c.id);
              return (
                <button
                  key={c.id}
                  data-testid={`collab-${c.id}`}
                  onClick={() => toggleActive(c.id)}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded hover:bg-zinc-200 transition-colors text-left"
                >
                  <img src={silo(c.gender, active)} alt="" className="w-7 h-7 object-contain" />
                  <span className={`text-sm ${active ? "text-green-600 font-semibold" : "text-zinc-800"}`}>{c.name || "Unnamed"}</span>
                </button>
              );
            })}
            {/* Human coordinator */}
            <button
              data-testid="collab-human"
              onClick={() => setHumanActive((v) => !v)}
              className="w-full flex items-center gap-3 px-2 py-2 rounded hover:bg-zinc-200 transition-colors text-left border-t border-zinc-300 mt-2 pt-3"
            >
              <img src={silo("male", humanActive)} alt="" className="w-7 h-7 object-contain" />
              <span className={`text-sm ${humanActive ? "text-green-600 font-semibold" : "text-zinc-800"}`}>{humanName}</span>
              <span className="text-[9px] uppercase text-zinc-400 ml-auto">Human</span>
            </button>
          </div>
          <div className="p-3 border-t border-zinc-300">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-zinc-700 uppercase">All Team</span>
              <div className="flex gap-2">
                <button data-testid="activate-all" onClick={activateAll} title="Activate all" className="hover:scale-110 transition-transform">
                  <img src={PWR("green-plus")} alt="+" className="w-7 h-7" />
                </button>
                <button data-testid="deactivate-all" onClick={deactivateAll} title="Deactivate all" className="hover:scale-110 transition-transform">
                  <img src={PWR("minus-black")} alt="-" className="w-7 h-7" />
                </button>
              </div>
            </div>
          </div>
          <button onClick={() => { setEditingProject(null); setShowProjectPanel(true); }} data-testid="digmoon-btn" className="flex items-center gap-2 px-4 py-3 bg-white hover:bg-zinc-100 transition-colors border-t border-zinc-300">
            <img src="/pwr/digmoon-white.jpg" alt="Dig Moon" className="w-8 h-8 object-contain" />
            <span className="text-xs font-bold uppercase tracking-wide text-zinc-800">Add New Project</span>
          </button>
        </aside>

        {/* CENTER: Command Centre Chat */}
        <main className="flex-1 bg-[#eef4fb] p-4 flex flex-col min-h-0 relative">
          <div className="bg-white rounded-2xl border-2 border-blue-200 flex-1 flex flex-col min-h-0 shadow-sm">
            <div className="px-5 py-3 border-b border-blue-100 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-[11px] font-bold uppercase tracking-wide text-zinc-500 shrink-0">{saveSource}</span>
                <span className="text-zinc-300 shrink-0">·</span>
                <span data-testid="chat-project-title" className="bg-zinc-100 rounded-full px-4 py-1.5 font-bold text-sm shrink-0" style={{ color: activeProject ? "#16a34a" : "#1e3a8a" }}>
                  {activeProject ? activeProject.name : "No Project Selected"}
                </span>
                <span className="text-zinc-300 shrink-0">·</span>
                <input
                  data-testid="chat-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject…"
                  className="min-w-0 flex-1 bg-transparent border-b border-zinc-200 px-2 py-1 text-sm text-zinc-700 focus:border-blue-400 focus:outline-none placeholder:text-zinc-400"
                />
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-zinc-400">{activeCount} active</span>
                <button data-testid="project-log-btn" onClick={() => setShowLog(true)} title="Open the Live Project Log — open issues across ALL your projects" className="h-9 px-3 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center gap-1.5 hover:bg-amber-600 transition-colors disabled:opacity-50">
                  <ClipboardText size={16} weight="fill" /> Project Log
                </button>
                <button data-testid="save-chat-btn" onClick={saveChat} title="Chat Summary — save a detailed AI memory file so the next session picks up where you left off" className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors">
                  <ChatText size={18} weight="fill" />
                </button>
              </div>
            </div>

            {/* messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {messages.length === 0 ? (
                <p className="text-sm text-zinc-400 text-center mt-10">No messages yet. Post to S.I.M.O.N. below.</p>
              ) : messages.map((m) => (
                m.type === "summary" ? (
                  <div key={m.id} data-testid="chat-summary-block" className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-zinc-800 shadow-sm">
                    <div className="flex items-center gap-2 mb-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">
                      <ListBullets size={14} weight="fill" /> Pinned Chat Summary
                    </div>
                    <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
                  </div>
                ) : m.author === "SYSTEM" ? (
                  <div key={m.id} data-testid={`msg-stamp-${m.id}`} className="flex items-center gap-3 my-2 text-[11px] uppercase tracking-widest text-zinc-400">
                    <span className="flex-1 h-px bg-zinc-200" />
                    <span className="font-mono">{m.text}</span>
                    <span className="flex-1 h-px bg-zinc-200" />
                  </div>
                ) : (
                <div key={m.id} data-testid={`msg-${m.id}`} className="text-sm">
                  <span className={`font-bold ${m.author === humanName ? "text-blue-600" : "text-zinc-900"}`}>@{m.author}:</span>{" "}
                  <span className="text-zinc-700 whitespace-pre-wrap">{m.text}</span>
                  {m.type !== "chat" && <span className="ml-2 text-[10px] uppercase text-white bg-blue-500 px-1.5 py-0.5 rounded">{m.type}</span>}
                  {m.image && (
                    <div className="mt-1.5">
                      <img src={imgSrc(m.image)} alt="attachment" data-testid={`msg-image-${m.id}`} className="max-h-64 rounded-lg border border-zinc-200" />
                    </div>
                  )}
                  {!m.image && m.file_path && (
                    <a
                      href={imgSrc(m.file_path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`msg-file-${m.id}`}
                      className="mt-1.5 inline-flex items-center gap-2 bg-zinc-100 border border-zinc-200 rounded-lg px-3 py-2 text-xs text-blue-700 hover:bg-blue-50 transition-colors max-w-full"
                    >
                      <Paperclip size={14} className="shrink-0" />
                      <span className="truncate">{m.file_name || "Download file"}</span>
                    </a>
                  )}
                </div>
                )
              ))}
              {typingName && (
                <div data-testid="typing-indicator" className="text-sm text-zinc-400 italic flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> {typingName} is responding...
                </div>
              )}
            </div>

            {/* composer */}
            <div className="border-t border-blue-100 p-4">
              {outOfCredits ? (
                <div data-testid="credits-empty-warning" className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
                  <span className="text-xs text-amber-800 font-semibold">You're out of S.I.M.O.N. credits. Subscribe or top up to keep chatting.</span>
                  <button data-testid="topup-link" onClick={() => navigate("/store")} className="shrink-0 text-xs font-bold bg-amber-500 text-white rounded-full px-3 py-1.5 hover:bg-amber-600 transition-colors">Top Up</button>
                </div>
              ) : (
                <div className="mb-2 text-[11px] text-zinc-500 text-right" data-testid="credits-count">S.I.M.O.N. credits: <span className="font-bold text-zinc-800">{simonCredits}</span></div>
              )}
              {composer !== "chat" && (
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <select value={taskFields.mention} onChange={(e) => setTaskFields({ ...taskFields, mention: e.target.value })} className="border border-zinc-300 rounded px-3 py-2 text-sm">
                    <option value="">@ Collaborator</option>
                    {collaborators.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                  <input placeholder="To Do / task" value={taskFields.to_do} onChange={(e) => setTaskFields({ ...taskFields, to_do: e.target.value })} className="border border-zinc-300 rounded px-3 py-2 text-sm" />
                  <input type="date" value={taskFields.start_date} onChange={(e) => setTaskFields({ ...taskFields, start_date: e.target.value })} className="border border-zinc-300 rounded px-3 py-2 text-sm" />
                  <input type="date" value={taskFields.end_date} onChange={(e) => setTaskFields({ ...taskFields, end_date: e.target.value })} className="border border-zinc-300 rounded px-3 py-2 text-sm" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && composer === "chat" && send()}
                  onPaste={onPaste}
                  disabled={composer === "chat" && outOfCredits}
                  placeholder={composer === "chat" ? (outOfCredits ? "Out of credits — top up to chat" : "Type or paste an image / message...") : `Add ${composer} details above, then send`}
                  className="flex-1 border border-zinc-300 rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none disabled:bg-zinc-100 disabled:cursor-not-allowed"
                />
                <input ref={fileInputRef} type="file" onChange={onPickFile} className="hidden" data-testid="chat-file-input" />
                <button onClick={() => fileInputRef.current?.click()} title="Attach a file" data-testid="attach-btn" className="text-zinc-400 hover:text-blue-600 transition-colors"><Paperclip size={20} /></button>
                <button data-testid="send-btn" onClick={send} disabled={composer === "chat" && outOfCredits} className="w-10 h-10 rounded-lg bg-green-700 text-white flex items-center justify-center hover:bg-green-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  <PaperPlaneRight size={18} weight="fill" />
                </button>
              </div>
              {/* function bar */}
              <div className="flex items-center gap-5 mt-3 text-xs">
                <button data-testid="composer-task" onClick={() => setComposer(composer === "task" ? "chat" : "task")} className={`flex items-center gap-1.5 ${composer === "task" ? "text-blue-600 font-bold" : "text-zinc-600"}`}>
                  <img src={PWR("task")} alt="" className="w-5 h-5 object-contain" /> Task/Assignment
                </button>
                <button data-testid="composer-research" onClick={() => setComposer(composer === "research" ? "chat" : "research")} className={`flex items-center gap-1.5 ${composer === "research" ? "text-blue-600 font-bold" : "text-zinc-600"}`}>
                  <img src={PWR("research")} alt="" className="w-5 h-5 object-contain" /> Research & Marketing
                </button>
                <button onClick={() => setShowDocs(true)} data-testid="open-docs" className="flex items-center gap-1.5 text-zinc-600 hover:text-blue-600">
                  <img src={PWR("folder-black")} alt="" className="w-5 h-5 object-contain" /> Documentation
                </button>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-zinc-400">Save as:</span>
                  <select data-testid="save-source" value={saveSource} onChange={(e) => setSaveSource(e.target.value)} className="border border-zinc-300 rounded px-2 py-1 text-xs">
                    {(meta.source_types || []).map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* RIGHT: Projects */}
        <aside className={`w-64 bg-blue-800 flex flex-col shrink-0 ${showSettings || showProjectPanel ? "opacity-60" : ""}`}>
          <div className="px-4 py-3 text-center border-b border-blue-700">
            <h2 className="font-bold text-white uppercase tracking-wide underline">Projects</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {projects.map((p) => (
              <div key={p.id} data-testid={`project-${p.id}`} className="bg-blue-900/40 rounded-lg p-3 hover:bg-blue-900/70 transition-colors cursor-pointer flex items-start gap-2"
                onClick={() => (selectProjectsMode ? selectProject(p) : (setEditingProject(p), setShowProjectPanel(true)))}>
              {selectProjectsMode && (
                  <input type="radio" checked={activeProject?.id === p.id} readOnly className="mt-1 accent-green-400" data-testid={`project-radio-${p.id}`} />
                )}
                <span className="w-3 h-3 rounded-full mt-1 shrink-0" style={{ backgroundColor: STATUS_COLORS[p.status] || "#9ca3af" }} />
                <div>
                  <p className="font-bold text-white text-sm leading-tight">{p.name}</p>
                  <p className="text-blue-200 text-xs">{p.status}</p>
                </div>
              </div>
            ))}
            {projects.length === 0 && <p className="text-blue-200 text-xs p-2">No projects. Add one via Dig Moon.</p>}
          </div>
          <button data-testid="select-projects-btn" onClick={() => setSelectProjectsMode((v) => !v)} className="flex items-center justify-center gap-2 py-3 border-t border-blue-700 hover:bg-blue-900/50 transition-colors">
            <img src={PWR(selectProjectsMode ? "select-projects-green" : "select-projects")} alt="" className="w-6 h-6 object-contain" style={selectProjectsMode ? {} : { filter: "brightness(0) invert(1)" }} />
            <span className={`text-xs font-bold uppercase ${selectProjectsMode ? "text-green-400" : "text-white"}`}>{selectProjectsMode ? "Selecting..." : "Select Projects"}</span>
          </button>
        </aside>
      </div>

      {/* ===== Modals ===== */}
      {showSettings && (
        <SettingsModal
          collaborators={collaborators} meta={meta} cards={cards}
          onClose={() => { setShowSettings(false); loadAll(); }}
          reload={loadAll}
          onViewProfile={(c) => setProfileCard(c)}
        />
      )}
      {showProjectPanel && (
        <ProjectModal
          meta={meta} project={editingProject}
          onClose={() => { setShowProjectPanel(false); loadAll(); }}
        />
      )}
      {showDocs && <DocsModal onClose={() => setShowDocs(false)} />}
      {profileCard && <ProfileModal collaborator={profileCard} onClose={() => setProfileCard(null)} />}
      {showLog && (
        <ProjectLogPanel activeProject={activeProject} onClose={() => setShowLog(false)} />
      )}
    </div>
  );
}

/* ---------- Live Project Log — auto-extracted from chats, across ALL projects ---------- */
const KIND_TAG = {
  task: { label: "Task", cls: "bg-blue-100 text-blue-700" },
  pending: { label: "Needs Approval", cls: "bg-amber-100 text-amber-700" },
  issue: { label: "Open Issue", cls: "bg-red-100 text-red-700" },
  action: { label: "Action Item", cls: "bg-purple-100 text-purple-700" },
};

function MemoryView({ pid }) {
  const [mem, setMem] = useState(null);
  useEffect(() => { api.get(`/pwr/projects/${pid}/memory`).then((r) => setMem(r.data)).catch((e) => toast.error(apiError(e))); }, [pid]);
  if (!mem) return <p className="px-4 py-3 text-xs text-zinc-400">Loading memory…</p>;
  return (
    <div data-testid={`log-memory-${pid}`} className="px-4 py-3 bg-indigo-50 border-t border-indigo-100">
      <p className="text-[10px] font-bold uppercase tracking-wide text-indigo-700 mb-1">AI Memory File {mem.updated_at ? `· updated ${new Date(mem.updated_at).toLocaleString()}` : ""}</p>
      <p className="text-[13px] text-zinc-800 whitespace-pre-wrap leading-relaxed">{mem.memory_file || "No memory file yet — it is created when you save a chat with the Chat Summary button."}</p>
    </div>
  );
}

function LogItemRow({ it, pid, i, onResolve, onApprove, onRemove }) {
  const tag = KIND_TAG[it.kind] || KIND_TAG.task;
  return (
    <div className="flex items-start gap-3 px-4 py-2.5">
      <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 ${tag.cls}`}>{tag.label}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-zinc-800 leading-snug">{it.text}</p>
        {(it.assignee || it.requested_by || it.reason) && (
          <p className="text-[11px] text-zinc-500 mt-0.5">{it.requested_by ? `by ${it.requested_by} ` : ""}{it.assignee ? `→ ${it.assignee} ` : ""}{it.reason ? `· ${it.reason}` : ""}</p>
        )}
      </div>
      <div className="flex gap-1.5 shrink-0">
        {it.kind === "pending" && (
          <>
            <button data-testid={`task-approve-${it.id}`} onClick={() => onApprove(it.id)} title="Approve" className="w-7 h-7 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600"><Check size={14} weight="bold" /></button>
            <button data-testid={`task-reject-${it.id}`} onClick={() => onRemove(it.id)} title="Reject" className="w-7 h-7 rounded-full bg-zinc-200 text-zinc-600 flex items-center justify-center hover:bg-red-100 hover:text-red-500"><X size={14} weight="bold" /></button>
          </>
        )}
        {it.kind !== "pending" && (
          <button data-testid={`item-resolve-${pid}-${i}`} onClick={() => onResolve(it)} title="Mark resolved" className="w-7 h-7 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600"><Check size={14} weight="bold" /></button>
        )}
      </div>
    </div>
  );
}

function LogProjectCard({ p, actions }) {
  const [showDec, setShowDec] = useState(false);
  const [showMem, setShowMem] = useState(false);
  return (
    <div data-testid={`log-project-${p.project_id}`} className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-zinc-50 border-b border-zinc-200">
        <span className="font-bold text-sm text-zinc-900">{p.project_name}</span>
        <div className="flex items-center gap-3">
          {p.decisions.length > 0 && (
            <button data-testid={`log-decisions-toggle-${p.project_id}`} onClick={() => setShowDec((v) => !v)} className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900">{showDec ? "Hide" : "Show"} {p.decisions.length} decision{p.decisions.length > 1 ? "s" : ""}</button>
          )}
          <button data-testid={`log-memory-toggle-${p.project_id}`} onClick={() => setShowMem((v) => !v)} className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"><Brain size={13} weight="fill" /> AI Memory</button>
          <span className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">{p.status} · {p.items.length} open</span>
        </div>
      </div>
      {p.no_history ? (
        <p data-testid={`log-nohistory-${p.project_id}`} className="text-[13px] text-zinc-400 italic px-4 py-3">NO HISTORY — idea logged, nothing noted yet.</p>
      ) : (
        <div className="divide-y divide-zinc-100">
          {p.items.map((it, i) => <LogItemRow key={it.id || `${it.kind}-${i}`} it={it} pid={p.project_id} i={i} {...actions} />)}
          {p.items.length === 0 && <p className="text-[13px] text-zinc-400 italic px-4 py-3">Nothing open — all clear.</p>}
        </div>
      )}
      {showDec && (
        <div data-testid={`log-decisions-${p.project_id}`} className="border-t border-emerald-100 bg-emerald-50/60 px-4 py-2.5 space-y-1">
          {p.decisions.map((d) => <p key={d.id} className="text-[13px] text-zinc-800"><span className="text-[10px] font-bold uppercase text-emerald-700 mr-2">Decision</span>{d.text}</p>)}
        </div>
      )}
      {showMem && <MemoryView pid={p.project_id} />}
    </div>
  );
}

function ProjectLogPanel({ activeProject, onClose }) {
  const [data, setData] = useState({ projects: [], total_open: 0 });
  const [reading, setReading] = useState(false);

  const load = useCallback(() => api.get("/pwr/open-items").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => {
    (async () => {
      if (activeProject) {
        setReading(true);
        try {
          const { data: r } = await api.post(`/pwr/projects/${activeProject.id}/log/extract`, {}, { timeout: 90000 });
          if (r.added || r.resolved) toast.success(`Log updated · ${r.added} new · ${r.resolved} resolved`);
        } catch (e) { toast.error(apiError(e)); }
        setReading(false);
      }
      load();
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const actions = {
    onResolve: async (it) => {
      try {
        if (it.log_item) await api.post(`/pwr/log-items/${it.id}/resolve`);
        else await api.put(`/pwr/tasks/${it.id}`, { status: "Done" });
        load(); toast.success("Resolved");
      } catch (e) { toast.error(apiError(e)); }
    },
    onApprove: async (id) => { try { await api.post(`/pwr/tasks/${id}/approve`); load(); toast.success("Approved"); } catch (e) { toast.error(apiError(e)); } },
    onRemove: async (id) => { try { await api.delete(`/pwr/tasks/${id}`); load(); } catch (e) { toast.error(apiError(e)); } },
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="project-log-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative h-full w-full max-w-3xl bg-zinc-100 shadow-2xl flex flex-col animate-in slide-in-from-right">
        <div className="bg-black px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <ClipboardText size={22} weight="duotone" className="text-amber-400" />
            <span className="font-bold">Project Log — All Projects</span>
            <span data-testid="open-total" className="ml-1 text-xs font-bold bg-amber-500 text-black rounded-full px-2 py-0.5">{data.total_open} open</span>
          </div>
          <button onClick={onClose} data-testid="log-close" className="text-white hover:text-red-400"><X size={20} weight="bold" /></button>
        </div>
        <div className="bg-white border-b border-zinc-300 px-4 py-3">
          {reading ? (
            <p data-testid="log-reading" className="text-xs text-blue-700 font-semibold flex items-center gap-2"><CircleNotch size={14} className="animate-spin" /> Reading “{activeProject?.name}” chat for action items, decisions and open issues…</p>
          ) : (
            <p className="text-[11px] text-zinc-500">Filled in automatically from your chats each time you open this log. Items stay open until a later chat shows they're fixed, or you tick them off.</p>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {data.projects.length === 0 && <p className="text-sm text-zinc-500 text-center py-10">No projects yet. Create one in the S.I.M.O.N. board.</p>}
          {data.projects.map((p) => <LogProjectCard key={p.project_id} p={p} actions={actions} />)}
        </div>
      </div>
    </div>
  );
}

/* ---------- Settings Modal (AI Collaborators table) ---------- */
function SettingsModal({ collaborators, meta, cards, onClose, reload, onViewProfile }) {
  const [rows, setRows] = useState(collaborators.map((c) => ({ ...c })));
  const [docsCollab, setDocsCollab] = useState(null);
  useEffect(() => setRows(collaborators.map((c) => ({ ...c }))), [collaborators]);

  const addRow = async () => {
    if (rows.length >= (meta.max_collaborators || 8)) { toast.error("Max 8 collaborators"); return; }
    try { await api.post("/pwr/collaborators", { name: "", gender: "male", specialties: "", role: "", model: "" }); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const save = async (row) => {
    if (!row.id) { toast.error("Row not ready yet — reopen Settings and try again."); reload(); return; }
    try { await api.put(`/pwr/collaborators/${row.id}`, {
      name: row.name, gender: row.gender || "male", specialties: row.specialties || "",
      role: row.role || "", model: row.model || "", engine: row.engine || "Claude",
      speak_order: Number(row.speak_order) || 0,
      linked_card_id: row.linked_card_id || null }); }
    catch (e) {
      if (e?.response?.status === 404) { toast.error("This collaborator no longer exists — refreshing."); reload(); }
      else { toast.error(apiError(e)); }
    }
  };
  const del = async (r, i) => {
    const name = (r.name && r.name.trim()) || "this collaborator";
    if (!r.id) { setRows(rows.filter((_, idx) => idx !== i)); return; }  // unsaved blank row → remove locally
    if (!window.confirm(`Permanently delete "${name}"?\n\nThis also deletes ALL of their ID documents (AI ID Card, Scope Card, Role Packet, Operational AI Resume) and CANNOT be undone.`)) return;
    try { await api.delete(`/pwr/collaborators/${r.id}`); toast.success(`Deleted ${name}`); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setField = (i, k, v) => { const n = [...rows]; n[i] = { ...n[i], [k]: v }; setRows(n); };
  const openDocs = (r) => { if (!r.id) { toast.error("Name the collaborator first, then open documents."); return; } setDocsCollab(r); };

  return (
    <Overlay title="Application Settings" onClose={onClose} wide>
      <div className="bg-white rounded-xl p-6">
        <h3 className="text-blue-600 font-bold text-lg mb-4">AI - Collaborators</h3>
        <div className="grid grid-cols-[44px_2fr_1.4fr_1fr_0.9fr_0.9fr_40px_28px] gap-2 text-xs font-bold text-zinc-700 border-b border-zinc-200 pb-2 mb-2">
          <span title="Speaking order">Order</span><span className="underline">Collaborator(s)</span><span className="underline">Specialist Area</span>
          <span className="underline">Role</span><span className="underline">AI Identity</span><span className="underline text-green-600">AI Engine ID</span><span className="underline text-center">OAR</span><span></span>
        </div>
        <datalist id="simon-roles">{(meta.roles || []).map((x) => <option key={x} value={x} />)}</datalist>
        <datalist id="simon-models">{(meta.models || []).map((x) => <option key={x} value={x} />)}</datalist>
        {rows.map((r, i) => (
          <div key={r.id} data-testid={`settings-row-${i}`} className="grid grid-cols-[44px_2fr_1.4fr_1fr_0.9fr_0.9fr_40px_28px] gap-2 items-center py-1.5">
            <input type="number" min="1" max="8" value={r.speak_order || ""} onChange={(e) => setField(i, "speak_order", e.target.value)} onBlur={() => save(r)} placeholder={String(i + 1)} title="Speaking order (1-8) — sets reply sequence" data-testid={`collab-order-${i}`} className="bg-zinc-100 rounded px-1 py-1.5 text-sm w-full text-center" />
            <div className="flex items-center gap-1">
              <button title="Toggle gender" onClick={() => { const g = r.gender === "female" ? "male" : "female"; setField(i, "gender", g); save({ ...r, gender: g }); }} className="shrink-0"><img src={silo(r.gender, false)} alt="" className="w-6 h-6" /></button>
              <input value={r.name || ""} onChange={(e) => setField(i, "name", e.target.value)} onBlur={() => save(r)} placeholder="AI name" className="bg-zinc-100 rounded px-2 py-1.5 text-sm w-full" data-testid={`collab-name-${i}`} />
            </div>
            <input value={r.specialties || ""} onChange={(e) => setField(i, "specialties", e.target.value)} onBlur={() => save(r)} placeholder="e.g. Legal, Engineering" className="bg-zinc-100 rounded px-2 py-1.5 text-sm" />
            <input list="simon-roles" value={r.role || ""} onChange={(e) => setField(i, "role", e.target.value)} onBlur={() => save(r)} placeholder="Role" title="Pick a role or type your own" data-testid={`collab-role-${i}`} className="bg-zinc-100 rounded px-2 py-1.5 text-sm" />
            <input list="simon-models" value={r.model || ""} onChange={(e) => setField(i, "model", e.target.value)} onBlur={() => save(r)} placeholder="AI Identity" title="AI Identity (badge) — pick or type your own" data-testid={`collab-identity-${i}`} className="bg-zinc-100 rounded px-2 py-1.5 text-sm" />
            <select value={r.engine || "Claude"} onChange={(e) => { setField(i, "engine", e.target.value); save({ ...r, engine: e.target.value }); }} className="bg-green-50 border border-green-200 rounded px-2 py-1.5 text-sm text-green-800" title="Live engine that powers replies" data-testid={`engine-${i}`}>
              {(meta.engines || ["Claude", "ChatGPT", "Gemini"]).map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <button onClick={() => openDocs(r)} title="Open ID documents" data-testid={`docs-btn-${i}`} className="justify-self-center text-blue-600 hover:text-blue-800 hover:scale-110 transition-transform">
              <IdentificationBadge size={26} weight="duotone" />
            </button>
            <button onClick={() => del(r, i)} title="Delete collaborator (asks for confirmation)" className="justify-self-center hover:scale-110 transition-transform" data-testid={`del-collab-${i}`}><img src={PWR("red-minus")} alt="delete" className="w-6 h-6" /></button>
          </div>
        ))}
        <div className="flex items-center justify-between mt-4">
          <button onClick={addRow} data-testid="add-collab-btn" className="hover:scale-110 transition-transform"><img src={PWR("green-plus")} alt="add" className="w-8 h-8" /></button>
          <button onClick={onClose} data-testid="settings-save" className="bg-orange-500 text-white font-bold rounded-full px-6 py-2 hover:bg-orange-600 transition-colors">Save Information</button>
        </div>
        <div className="flex justify-center mt-6 pt-5 border-t border-zinc-200">
          <img src="/simon-globe-white.jpg" alt="S.I.M.O.N." data-testid="settings-globe" className="w-20 h-20 object-contain" />
        </div>
      </div>
      {docsCollab && (
        <CollaboratorDocsPanel
          collaborator={docsCollab} meta={meta} cards={cards}
          onClose={() => setDocsCollab(null)}
          onSaved={reload}
        />
      )}
    </Overlay>
  );
}

/* ---------- Collaborator Documents Side Panel (folder tabs) ---------- */
const DOC_TABS = [
  { key: "doc_id_card", label: "AI ID Card" },
  { key: "doc_company", label: "Company Profile" },
  { key: "doc_scope", label: "2nd Identity" },
  { key: "doc_role_packet", label: "2nd Identity Profile" },
  { key: "doc_oars", label: "Operational AI Resume (OARs)", globe: true },
];

// Shared Company Profile fields (single record per user, read by all agents).
const COMPANY_FIELDS = [
  { k: "company", label: "Company", expand: false },
  { k: "founder", label: "Founder", expand: false },
  { k: "vision", label: "Vision", expand: true },
  { k: "logic", label: "Logic", expand: true },
  { k: "expectations", label: "Expectations", expand: true },
  { k: "goal", label: "Goal", expand: true },
  { k: "company_team", label: "Company Team / Position", expand: true },
];

// Maps a saved customer AI iD Card (CARD_FIELDS) onto the main OAR field keys.
const CARD_TO_OAR = {
  who_i_am: "who_i_am", role_position_title: "role_title", behavior: "behavior",
  mode: "mode", style: "style", identity_of_ai: "identity_of_ai", ai_engine: "ai_engine",
  continuity_protocol: "continuity_protocol", company: "company", founder: "founder",
  team_members: "company_team", additional_information: "general_info",
};
const CARD_TEXT_LABELS = [
  ["who_i_am", "Who I am:"], ["role_position_title", "Role/Position/Title:"], ["behavior", "Behavior:"],
  ["mode", "Mode:"], ["style", "Style:"], ["identity_of_ai", "Identity of AI:"], ["ai_engine", "AI Engine:"],
  ["continuity_protocol", "Continuity Protocol:"], ["company", "Company:"], ["founder", "Founder:"],
  ["team_members", "Team Members:"], ["additional_information", "Additional Information:"],
];

// Operational AI Resume — 3 sections split by minimized dividers.
// expand:true fields render as amber (yellow) expandable text areas; the rest are blue static inputs.
// NOTE: existing field keys are preserved so no saved data is lost; only labels/panels changed.
const OARS_SECTIONS = [
  {
    key: "ai_profile", title: "AI Profile", detail: "Details",
    fields: [
      { k: "who_i_am", label: "Who I am:", expand: true },
      { k: "role_title", label: "Role/Position/Title:" },
      { k: "role_definition", label: "Role Definition:", expand: true },
      { k: "responsibilities", label: "Responsibilities:", expand: true },
      { k: "behavior", label: "Behavior:", expand: true },
      { k: "mode", label: "Mode:" },
      { k: "style", label: "Style:" },
      { k: "identity_of_ai", label: "AI Identity:" },
      { k: "ai_engine", label: "AI Engine:" },
    ],
  },
  {
    key: "rules_protocols", title: "AI Rules & Protocols", detail: "Details",
    fields: [
      { k: "continuity_protocol", label: "Continuity Protocol:", expand: true },
      { k: "collaboration_rules", label: "Collaboration Rules:", expand: true },
      { k: "communication_rules", label: "Communication Rules:", expand: true },
      { k: "escalation_rules", label: "Escalation Rules:", expand: true },
      { k: "general_info", label: "General Rules:", expand: true },
      { k: "projects", label: "Projects:", expand: true },
      { k: "additional_information", label: "Additional Information:", expand: true },
    ],
  },
  {
    key: "skill_scope", title: "Ai SkillScope", detail: "Details",
    fields: [
      { k: "additional_role_1", label: "Additional Role 1:" },
      { k: "additional_skills_1", label: "Additional Skills 1:", expand: true },
      { k: "additional_role_2", label: "Additional Role 2:" },
      { k: "additional_skills_2", label: "Additional Skills 2:", expand: true },
    ],
  },
];

function OarsResume({ value, onChange, activatable = false, activeKeys = [], onActivate, onDeactivate }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const active = new Set(activeKeys);
  return (
    <div data-testid="oars-resume" className="flex gap-0 divide-x-2 divide-zinc-300">
      {OARS_SECTIONS.map((sec) => (
        <div key={sec.key} className="flex-1 px-3 first:pl-0 last:pr-0 min-w-0">
          <div className="grid grid-cols-[minmax(72px,auto)_1fr] gap-2 mb-3 pb-1.5 border-b-2 border-zinc-800">
            <span className="text-xs font-bold tracking-wide text-zinc-900">{sec.title}</span>
            <span className="text-xs font-bold uppercase tracking-wide text-zinc-500">{sec.detail}</span>
          </div>
          <div className="space-y-2">
            {sec.fields.map((f) => {
              const on = !activatable || active.has(f.k);
              return (
                <div key={f.k} className="grid grid-cols-[minmax(72px,auto)_1fr] gap-2 items-start">
                  <label className={`text-[11px] font-semibold pt-1.5 leading-tight ${on ? "text-blue-800" : "text-zinc-400"}`}>{f.label}</label>
                  {activatable && !on ? (
                    <button
                      onClick={() => onActivate(f.k)}
                      data-testid={`oar2-add-${f.k}`}
                      title="Activate this field"
                      className="flex items-center justify-center gap-1 w-full border border-dashed border-zinc-300 rounded px-2 py-1.5 text-[11px] text-zinc-400 opacity-50 hover:opacity-100 hover:text-blue-600 hover:border-blue-300 transition"
                    >
                      <Plus size={12} weight="bold" /> Activate
                    </button>
                  ) : (
                    <div className="flex items-start gap-1">
                      {f.expand ? (
                        <textarea
                          data-testid={`oars-${f.k}`}
                          value={value[f.k] || ""}
                          onChange={(e) => set(f.k, e.target.value)}
                          rows={1}
                          placeholder="…"
                          className="flex-1 bg-amber-50 border border-amber-300 rounded px-2 py-1 text-xs text-zinc-800 resize-y min-h-[34px] focus:outline-none focus:border-amber-500"
                        />
                      ) : (
                        <input
                          data-testid={`oars-${f.k}`}
                          value={value[f.k] || ""}
                          onChange={(e) => set(f.k, e.target.value)}
                          placeholder="…"
                          className="flex-1 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 text-xs text-zinc-800 focus:outline-none focus:border-blue-400"
                        />
                      )}
                      {activatable && (
                        <button
                          onClick={() => onDeactivate(f.k)}
                          data-testid={`oar2-del-${f.k}`}
                          title="Deactivate this field"
                          className="shrink-0 mt-1 text-zinc-400 hover:text-red-500 transition"
                        >
                          <Minus size={16} weight="bold" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function CollaboratorDocsPanel({ collaborator, meta, cards = [], onClose, onSaved }) {
  const [tab, setTab] = useState("doc_id_card");
  const [docs, setDocs] = useState({
    doc_id_card: collaborator.doc_id_card || "",
    doc_scope: collaborator.doc_scope || "",
  });
  const parseOars = (raw) => { try { const d = JSON.parse(raw || "{}"); return d && typeof d === "object" ? d : {}; } catch { return {}; } };
  const [oars, setOars] = useState(parseOars(collaborator.doc_oars));
  const [oars2, setOars2] = useState(parseOars(collaborator.doc_role_packet));  // 2nd Identity Profile (activated fields)
  const [idCardActive, setIdCardActive] = useState(collaborator.doc_id_card_active !== false);
  const [showCardPicker, setShowCardPicker] = useState(false);
  const [role] = useState(collaborator.role || "");
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);           // shared Company Profile record
  const [uploads, setUploads] = useState(collaborator.doc_uploads || []);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/pwr/company-profile").then((r) => setCompany(r.data)).catch(() => setCompany({}));
  }, []);

  const templates = meta.doc_templates || {};

  const isOars = tab === "doc_oars";
  const isProfile2 = tab === "doc_role_packet";
  const isCompany = tab === "doc_company";
  const wide = isOars || isProfile2;

  const setDoc = (v) => setDocs((d) => ({ ...d, [tab]: v }));
  const setCompanyField = (k, v) => setCompany((c) => ({ ...(c || {}), [k]: v }));

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (file) e.target.value = "";
    if (!file) return;
    const ok = /\.(pdf|docx|doc|xlsx|xls)$/i.test(file.name);
    if (!ok) { toast.error("Accepted formats: PDF, DOCX, DOC, Excel (.xlsx/.xls)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/pwr/collaborators/${collaborator.id}/upload-doc`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setUploads(data.doc_uploads || []);
      toast.success(`Uploaded "${file.name}" — this agent can now read it`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const removeUpload = async (idx) => {
    try {
      const { data } = await api.delete(`/pwr/collaborators/${collaborator.id}/upload-doc/${idx}`);
      setUploads(data.doc_uploads || []);
    } catch (err) { toast.error(apiError(err)); }
  };

  const applyCard = async (cardId) => {
    try {
      const { data } = await api.get(`/cards/${cardId}`);
      const content = data.content || {};
      const text = CARD_TEXT_LABELS.map(([k, l]) => `${l} ${content[k] || ""}`).join("\n");
      setDocs((d) => ({ ...d, doc_id_card: text }));
      setOars((prev) => {
        const next = { ...prev };
        for (const [ck, ok] of Object.entries(CARD_TO_OAR)) {
          if (content[ck] && String(content[ck]).trim()) next[ok] = content[ck];
        }
        return next;
      });
      setIdCardActive(true);
      setShowCardPicker(false);
      toast.success("AI iD Card loaded — matching OAR fields filled");
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadTemplate = () => {
    if (isOars) {
      if (Object.values(oars).some((x) => x) && !window.confirm("Clear the Operational AI Resume?")) return;
      setOars({}); toast.info("OARs cleared — fill it in and save"); return;
    }
    if (isProfile2) {
      if (Object.keys(oars2).length && !window.confirm("Deactivate & clear all 2nd Identity Profile fields?")) return;
      setOars2({}); toast.info("Fields cleared"); return;
    }
    if (isCompany) {
      if (COMPANY_FIELDS.some((f) => (company || {})[f.k]) && !window.confirm("Clear the shared Company Profile?")) return;
      setCompany(Object.fromEntries(COMPANY_FIELDS.map((f) => [f.k, ""]))); toast.info("Cleared — fill it in and save"); return;
    }
    if (tab === "doc_id_card") { setShowCardPicker((v) => !v); return; }
    if (docs[tab] && !window.confirm("Replace the current document with the blank template?")) return;
    setDoc(templates[tab] || ""); toast.info("Template loaded — edit and save");
  };

  const footerLabel = isOars ? "Clear Resume"
    : isProfile2 ? "Clear Fields"
    : isCompany ? "Clear Fields"
    : tab === "doc_id_card" ? "Upload AI iD Card" : "Load Template";

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.patch(`/pwr/collaborators/${collaborator.id}/docs`, {
        doc_id_card: docs.doc_id_card, doc_scope: docs.doc_scope,
        doc_role_packet: JSON.stringify(oars2), doc_oars: JSON.stringify(oars),
        doc_id_card_active: idCardActive, role });
      if (company) {
        await api.put("/pwr/company-profile",
          Object.fromEntries(COMPANY_FIELDS.map((f) => [f.k, company[f.k] || ""])));
      }
      toast.success("Documents saved");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="docs-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative h-full w-full ${wide ? "max-w-5xl" : "max-w-2xl"} bg-zinc-100 shadow-2xl flex flex-col animate-in slide-in-from-right transition-all`}>
        {/* header */}
        <div className="bg-black px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <IdentificationBadge size={22} weight="duotone" className="text-blue-400" />
            <span className="font-bold">{collaborator.name || "Collaborator"} — Documents</span>
          </div>
          <button onClick={onClose} data-testid="docs-panel-close" className="text-white hover:text-red-400"><X size={20} weight="bold" /></button>
        </div>
        {/* folder tabs */}
        <div className="flex gap-1 px-4 pt-3 bg-zinc-100 flex-wrap">
          {DOC_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`doc-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-t-lg border border-b-0 transition-colors ${
                tab === t.key
                  ? "bg-white text-blue-700 border-zinc-300"
                  : "bg-zinc-200 text-zinc-500 border-transparent hover:bg-zinc-50"
              }`}
            >
              {t.globe && <img src="/simon-globe.png" alt="" className="w-5 h-5 object-contain" />}
              {t.label}
            </button>
          ))}
        </div>
        {/* document body */}
        <div className="flex-1 overflow-y-auto bg-white border-t border-zinc-300 p-5">
          {isOars ? (
            <>
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-zinc-200">
                <img src="/simon-globe.png" alt="" className="w-10 h-10 object-contain" />
                <div>
                  <h3 className="font-bold text-lg text-zinc-900">Operational AI Resume (OARs)</h3>
                  <p className="text-xs text-zinc-500">Amber fields expand · blue fields are single-line. Read live by the AI.</p>
                </div>
              </div>
              <OarsResume value={oars} onChange={setOars} />
            </>
          ) : isProfile2 ? (
            <>
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-zinc-200">
                <img src="/simon-globe.png" alt="" className="w-10 h-10 object-contain" />
                <div>
                  <h3 className="font-bold text-lg text-zinc-900">2nd Identity Profile</h3>
                  <p className="text-xs text-zinc-500">Same fields as the OAR. Click <b>+ Activate</b> to enable & edit a field, <b>−</b> to deactivate. Only activated fields are saved and read by the AI. Not affected by the AI iD Card upload.</p>
                </div>
              </div>
              <OarsResume
                value={oars2} onChange={setOars2} activatable activeKeys={Object.keys(oars2)}
                onActivate={(k) => setOars2((o) => ({ ...o, [k]: o[k] || "" }))}
                onDeactivate={(k) => setOars2((o) => { const n = { ...o }; delete n[k]; return n; })}
              />
            </>
          ) : isCompany ? (
            <>
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-zinc-200">
                <IdentificationBadge size={34} weight="duotone" className="text-blue-500" />
                <div>
                  <h3 className="font-bold text-lg text-zinc-900">Company Profile</h3>
                  <p className="text-xs text-zinc-500">A single shared record read by <b>all</b> your agents — editing it here updates it everywhere.</p>
                </div>
              </div>
              {company === null ? (
                <p className="text-sm text-zinc-500">Loading…</p>
              ) : (
                <div className="space-y-3" data-testid="company-profile-form">
                  {COMPANY_FIELDS.map((f) => (
                    <div key={f.k} className="grid grid-cols-[minmax(130px,auto)_1fr] gap-3 items-start">
                      <label className="text-xs font-semibold pt-2 leading-tight text-blue-800">{f.label}:</label>
                      {f.expand ? (
                        <textarea
                          data-testid={`company-${f.k}`}
                          value={company[f.k] || ""}
                          onChange={(e) => setCompanyField(f.k, e.target.value)}
                          rows={2} placeholder="…"
                          className="w-full bg-amber-50 border border-amber-300 rounded px-3 py-2 text-sm text-zinc-800 resize-y min-h-[48px] focus:outline-none focus:border-amber-500"
                        />
                      ) : (
                        <input
                          data-testid={`company-${f.k}`}
                          value={company[f.k] || ""}
                          onChange={(e) => setCompanyField(f.k, e.target.value)}
                          placeholder="…"
                          className="w-full bg-blue-50 border border-blue-200 rounded px-3 py-2 text-sm text-zinc-800 focus:outline-none focus:border-blue-400"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {tab === "doc_id_card" && (
                <>
                <div className="flex items-center justify-between gap-3 mb-3 pb-3 border-b border-zinc-200">
                  <div>
                    <h3 className="font-bold text-zinc-900">AI iD Card</h3>
                    <p className="text-xs text-zinc-500">Upload a saved card to auto-fill the main OAR. Toggle whether the AI reads this card.</p>
                  </div>
                  <button
                    type="button" role="switch" aria-checked={idCardActive}
                    data-testid="idcard-active-toggle"
                    onClick={() => setIdCardActive((v) => !v)}
                    className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold transition-colors shrink-0 ${idCardActive ? "bg-green-100 text-green-700" : "bg-zinc-200 text-zinc-500"}`}
                  >
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${idCardActive ? "bg-green-500" : "bg-zinc-400"}`} />
                    {idCardActive ? "Active" : "Inactive"}
                  </button>
                </div>
                {uploads.length > 0 && (
                  <div className="mb-3" data-testid="uploaded-docs-list">
                    <p className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-1">Uploaded Documents (read by this agent)</p>
                    <div className="space-y-1">
                      {uploads.map((u, i) => (
                        <div key={i} data-testid={`uploaded-doc-${i}`} className="flex items-center justify-between gap-2 text-sm bg-blue-50 border border-blue-200 rounded px-3 py-1.5">
                          <span className="flex items-center gap-2 text-zinc-800 truncate"><FileText size={14} className="shrink-0 text-blue-500" /><span className="truncate">{u.name}</span></span>
                          <button onClick={() => removeUpload(i)} data-testid={`remove-upload-${i}`} title="Remove" className="shrink-0 text-zinc-400 hover:text-red-500"><Trash size={15} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </>
              )}
              <textarea
                data-testid={`doc-textarea-${tab}`}
                value={docs[tab]}
                onChange={(e) => setDoc(e.target.value)}
                placeholder="Type this collaborator's document here…"
                className="w-full h-[calc(100vh-360px)] min-h-[260px] bg-white border border-zinc-200 rounded-lg p-4 text-sm text-zinc-800 font-mono leading-relaxed whitespace-pre-wrap focus:outline-none focus:border-blue-400 resize-none"
              />
            </>
          )}
        </div>
        {/* card picker (AI iD Card upload) */}
        {showCardPicker && (
          <div className="border-t border-zinc-200 bg-white px-5 py-3 max-h-48 overflow-y-auto" data-testid="card-picker">
            {cards.length === 0 ? (
              <p className="text-sm text-zinc-500">No saved AI iD Cards yet — create one in the Dashboard first.</p>
            ) : (
              <div className="space-y-1">
                <p className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-1">Choose a saved AI iD Card</p>
                {cards.map((c) => (
                  <button key={c.id} data-testid={`card-pick-${c.id}`} onClick={() => applyCard(c.id)}
                    className="w-full text-left text-sm px-3 py-2 rounded hover:bg-blue-50 border border-zinc-200 flex items-center justify-between transition-colors">
                    <span className="font-medium text-zinc-800">{c.project_name || "AI iD Card"}</span>
                    <span className="text-xs text-zinc-400">{c.prid}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {/* actions */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-zinc-300 bg-zinc-100">
          <button onClick={loadTemplate} data-testid="load-template-btn" className="flex items-center gap-2 text-sm font-bold text-zinc-600 border border-zinc-300 rounded-full px-4 py-2 hover:bg-white transition-colors">
            <FileText size={16} /> {footerLabel}
          </button>
          {tab === "doc_id_card" && (
            <>
              <input ref={fileInputRef} type="file" accept=".pdf,.docx,.doc,.xlsx,.xls"
                onChange={uploadDoc} className="hidden" data-testid="upload-documents-input" />
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                data-testid="upload-documents-btn"
                className="flex items-center gap-2 text-sm font-bold text-blue-700 border border-blue-300 rounded-full px-4 py-2 hover:bg-blue-50 transition-colors disabled:opacity-50">
                <UploadSimple size={16} /> {uploading ? "Uploading…" : "Upload Documents"}
              </button>
            </>
          )}
          <button onClick={saveAll} disabled={saving} data-testid="docs-save-btn" className="bg-orange-500 text-white font-bold rounded-full px-6 py-2 hover:bg-orange-600 transition-colors disabled:opacity-50">
            {saving ? "Saving…" : "Save Documents"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Project Details & Status Modal ---------- */
function ProjectModal({ meta, project, onClose }) {
  const [f, setF] = useState(project || { name: "", platform: "", details: "", internal_external: "", target_audience: "", status: "Concept", ai_read_mode: "tight" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    try {
      if (project?.id) await api.put(`/pwr/projects/${project.id}`, f);
      else await api.post("/pwr/projects", f);
      toast.success("Project saved"); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => {
    if (!project?.id || !window.confirm("Delete this project?")) return;
    try { await api.delete(`/pwr/projects/${project.id}`); toast.success("Deleted"); onClose(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Overlay title="Project Details & Status" onClose={onClose} tint="blue">
      <div className="bg-blue-100 rounded-xl p-6 space-y-3">
        {[["Project Name:", "name"], ["Platform:", "platform"]].map(([l, k]) => (
          <div key={k}><label className="text-sm font-semibold text-zinc-800">{l}</label>
            <input value={f[k]} onChange={set(k)} data-testid={`proj-${k}`} className="mt-1 w-full bg-white rounded px-3 py-2 text-sm" /></div>
        ))}
        <div><label className="text-sm font-semibold text-zinc-800">Project Details:</label>
          <textarea value={f.details} onChange={set("details")} rows={3} data-testid="proj-details" className="mt-1 w-full bg-white rounded px-3 py-2 text-sm" /></div>
        {[["Internal/External Use:", "internal_external"], ["Target Audience:", "target_audience"]].map(([l, k]) => (
          <div key={k}><label className="text-sm font-semibold text-zinc-800">{l}</label>
            <input value={f[k]} onChange={set(k)} className="mt-1 w-full bg-white rounded px-3 py-2 text-sm" /></div>
        ))}
        <div><label className="text-sm font-semibold text-zinc-800">Project Status:</label>
          <select value={f.status} onChange={set("status")} data-testid="proj-status" className="mt-1 w-full bg-white rounded px-3 py-2 text-sm">
            {(meta.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select></div>
        <div>
          <label className="text-sm font-semibold text-zinc-800">AI Reading Mode:</label>
          <select value={f.ai_read_mode || "tight"} onChange={set("ai_read_mode")} data-testid="proj-read-mode" className="mt-1 w-full bg-white rounded px-3 py-2 text-sm">
            <option value="tight">Tight — cheaper (summaries only, large files clipped)</option>
            <option value="full">Full — high-fidelity (loads whole files, higher token cost)</option>
          </select>
          <p className="text-[11px] text-zinc-500 mt-1">
            Controls how much documentation your AIs load per reply. "Full" reads whole specs (up to ~40k chars/file); "Tight" keeps credits low.
          </p>
        </div>
        <div className="flex items-center justify-between pt-2">
          {project?.id ? <button onClick={del} className="text-red-600 text-sm flex items-center gap-1"><Trash size={16} /> Delete</button> : <span />}
          <button onClick={submit} data-testid="proj-save" className="bg-orange-500 text-white font-bold rounded-full px-6 py-2 hover:bg-orange-600 transition-colors mx-auto">Update Info</button>
        </div>
      </div>
    </Overlay>
  );
}

/* ---------- PWR Documentation (activity log) ---------- */
const TASK_STATUSES = ["To Do", "In Progress", "In Review", "Done", "Must Read"];
const DOC_STATUSES = ["New", "Read", "Must Read", "Completed"];
const DOC_STATUS_STYLE = {
  "To Do": "bg-zinc-100 text-zinc-700 border-zinc-300",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-300",
  "In Review": "bg-blue-50 text-blue-700 border-blue-300",
  "Done": "bg-green-50 text-green-700 border-green-300",
  "New": "bg-zinc-100 text-zinc-700 border-zinc-300",
  "Read": "bg-blue-50 text-blue-700 border-blue-300",
  "Must Read": "bg-red-50 text-red-700 border-red-300",
  "Completed": "bg-green-50 text-green-700 border-green-300",
};
function DocsModal({ onClose }) {
  const [rows, setRows] = useState([]);
  const [openDoc, setOpenDoc] = useState(null);
  const [view, setView] = useState("list");          // list | tree
  const [showArchive, setShowArchive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fProject, setFProject] = useState("");
  const [fSubject, setFSubject] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const reload = () => api.get("/pwr/activity").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);

  const subjOf = (r) => (r.subject && r.subject.trim()) ? r.subject.trim() : "General";
  const projects = [...new Set(rows.map((r) => r.project_name).filter(Boolean))].sort();
  const subjects = [...new Set(rows.map(subjOf))].sort();

  const inRange = (r) => {
    const d = (r.created_at || "").slice(0, 10);
    if (fFrom && d && d < fFrom) return false;
    if (fTo && d && d > fTo) return false;
    return true;
  };
  const visible = rows.filter((r) =>
    (showArchive ? true : !r.archived) &&
    (!fProject || r.project_name === fProject) &&
    (!fSubject || subjOf(r) === fSubject) &&
    inRange(r)
  );

  const updateStatus = async (row, value) => {
    setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, doc_status: value } : x)));
    try {
      await api.patch(`/pwr/activity/${row.id}/status`, { doc_status: value });
      toast.success(`Status → ${value}`);
    } catch {
      toast.error("Could not update status");
      reload();
    }
  };

  const consolidate = async (project_id, subject) => {
    if (!project_id) { toast.error("This entry has no project to consolidate."); return; }
    if (!window.confirm(`Consolidate all "${subject}" chats into a single MUST READ briefing? The folded chats move to Archive.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pwr/consolidate", { project_id, subject });
      toast.success(`Consolidated ${data.folded} chat(s) → MUST READ`);
      await reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const clearFilters = () => { setFProject(""); setFSubject(""); setFFrom(""); setFTo(""); };

  // group filtered rows: project -> subject -> [docs]
  const tree = {};
  visible.forEach((r) => {
    const p = r.project_name || "—";
    const s = subjOf(r);
    tree[p] = tree[p] || { project_id: r.project_id, subjects: {} };
    if (r.project_id) tree[p].project_id = r.project_id;
    tree[p].subjects[s] = tree[p].subjects[s] || [];
    tree[p].subjects[s].push(r);
  });

  const statusPill = (r) => {
    const opts = r.status_kind === "task" ? TASK_STATUSES : DOC_STATUSES;
    const style = DOC_STATUS_STYLE[r.doc_status] || "bg-zinc-100 text-zinc-700 border-zinc-300";
    return (
      <select
        data-testid={`doc-status-${r.id}`}
        value={r.doc_status}
        onChange={(e) => updateStatus(r, e.target.value)}
        onClick={(e) => e.stopPropagation()}
        className={`rounded-full border px-2 py-1 text-xs font-semibold cursor-pointer focus:outline-none ${style}`}>
        {opts.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    );
  };

  const docButton = (r) => (
    <button
      onClick={() => setOpenDoc(r)}
      data-testid={`doc-open-${r.id}`}
      className="text-left text-blue-600 font-medium hover:text-blue-800 hover:underline truncate flex items-center gap-1.5"
      title="Open document"
    >
      {r.consolidated ? <ListBullets size={14} className="shrink-0 text-red-600" /> : <FileText size={14} className="shrink-0" />}
      <span className="truncate">{r.file_name}</span>
      {r.version > 1 && <span className="text-[10px] text-zinc-400">v{r.version}</span>}
      {r.archived && <span className="text-[9px] uppercase text-zinc-400 border border-zinc-300 rounded px-1">archived</span>}
    </button>
  );

  return (
    <Overlay title="S.I.M.O.N. – Documentation" onClose={onClose}>
      <div className="bg-white rounded-xl p-6">
        {/* toolbar */}
        <div className="flex flex-wrap items-end gap-3 pb-4 mb-3 border-b border-zinc-200">
          <div className="flex items-center gap-1.5 text-zinc-500"><FunnelSimple size={16} /><span className="text-xs font-bold uppercase">Filter</span></div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase text-zinc-400">Project</label>
            <select data-testid="filter-project" value={fProject} onChange={(e) => setFProject(e.target.value)} className="bg-zinc-100 rounded px-2 py-1.5 text-sm">
              <option value="">All</option>
              {projects.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase text-zinc-400">Subject</label>
            <select data-testid="filter-subject" value={fSubject} onChange={(e) => setFSubject(e.target.value)} className="bg-zinc-100 rounded px-2 py-1.5 text-sm">
              <option value="">All</option>
              {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase text-zinc-400">From</label>
            <input data-testid="filter-from" type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="bg-zinc-100 rounded px-2 py-1.5 text-sm" />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase text-zinc-400">To</label>
            <input data-testid="filter-to" type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="bg-zinc-100 rounded px-2 py-1.5 text-sm" />
          </div>
          <button onClick={clearFilters} className="text-xs text-zinc-500 underline py-2">Clear</button>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="toggle-archive" onClick={() => setShowArchive((v) => !v)} className={`flex items-center gap-1.5 text-xs font-bold rounded-full border px-3 py-2 transition-colors ${showArchive ? "bg-zinc-800 text-white border-zinc-800" : "text-zinc-600 border-zinc-300 hover:bg-zinc-50"}`}>
              <Archive size={14} /> {showArchive ? "Hide Archive" : "Show Archive"}
            </button>
            <button data-testid="toggle-view" onClick={() => setView((v) => (v === "list" ? "tree" : "list"))} className="flex items-center gap-1.5 text-xs font-bold rounded-full border border-blue-300 text-blue-700 px-3 py-2 hover:bg-blue-50 transition-colors">
              {view === "list" ? <><TreeStructure size={14} /> Consolidated View</> : <><FileText size={14} /> List View</>}
            </button>
          </div>
        </div>

        {visible.length === 0 && <p className="text-sm text-zinc-400 py-6 text-center">No documentation matches these filters.</p>}

        {/* LIST VIEW */}
        {view === "list" && visible.length > 0 && (
          <>
            <div className="grid grid-cols-[1fr_1.2fr_1fr_1.8fr_1fr_0.9fr] gap-3 text-xs font-bold text-blue-700 border-b border-zinc-200 pb-2">
              <span>Source</span><span>Project</span><span>Subject</span><span>File Name</span><span>Status</span><span>Date</span>
            </div>
            {visible.map((r) => (
              <div key={r.id} data-testid={`doc-${r.id}`} className={`grid grid-cols-[1fr_1.2fr_1fr_1.8fr_1fr_0.9fr] gap-3 text-sm py-2 border-b border-zinc-100 items-center hover:bg-blue-50/50 transition-colors ${r.archived ? "opacity-60" : ""}`}>
                <span className="text-xs font-semibold text-zinc-600">{r.source_type}{r.collaborator_name ? ` · ${r.collaborator_name}` : ""}</span>
                <span className="text-zinc-800 truncate">{r.project_name}</span>
                <span className="text-xs text-zinc-500 truncate">{subjOf(r)}</span>
                {docButton(r)}
                {statusPill(r)}
                <span className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </>
        )}

        {/* CONSOLIDATED TREE VIEW */}
        {view === "tree" && visible.length > 0 && (
          <div data-testid="docs-tree" className="text-sm">
            <div className="flex items-center gap-2 font-bold text-zinc-900 mb-1"><img src="/simon-globe-white.jpg" alt="" className="w-5 h-5 object-contain" /> S.I.M.O.N.</div>
            {Object.entries(tree).map(([proj, node]) => (
              <div key={proj} className="ml-3 border-l border-zinc-200 pl-4 py-1">
                <div className="flex items-center gap-2 font-semibold text-zinc-800">
                  <CaretDown size={12} className="text-zinc-400" /> {proj}
                </div>
                {Object.entries(node.subjects).map(([subj, docs]) => (
                  <div key={subj} className="ml-4 border-l border-zinc-200 pl-4 py-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2 text-zinc-700 font-medium"><CaretRight size={11} className="text-zinc-400" /> {subj} <span className="text-[10px] text-zinc-400">({docs.length})</span></span>
                      <button
                        data-testid={`consolidate-${proj}-${subj}`.replace(/\s+/g, "-")}
                        disabled={busy}
                        onClick={() => consolidate(node.project_id, subj)}
                        className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-red-600 border border-red-200 rounded-full px-2.5 py-1 hover:bg-red-50 transition-colors disabled:opacity-50"
                        title="Consolidate these chats into one MUST READ briefing"
                      >
                        <ArrowsIn size={12} /> {busy ? "…" : "Consolidate"}
                      </button>
                    </div>
                    <div className="mt-1 space-y-1">
                      {docs.sort((a, b) => (b.consolidated ? 1 : 0) - (a.consolidated ? 1 : 0)).map((r) => (
                        <div key={r.id} data-testid={`doc-${r.id}`} className={`flex items-center justify-between gap-3 pl-5 ${r.archived ? "opacity-60" : ""}`}>
                          {docButton(r)}
                          <div className="flex items-center gap-2 shrink-0">
                            {statusPill(r)}
                            <span className="text-[11px] text-zinc-400">{new Date(r.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      {openDoc && <DocEditorPanel doc={openDoc} onClose={() => setOpenDoc(null)} onSaved={reload} />}
    </Overlay>
  );
}

/* ---------- Document Reader / Editor (opens a Documentation row) ---------- */
function DocEditorPanel({ doc, onClose, onSaved }) {
  const [fileName, setFileName] = useState(doc.file_name || "");
  const [content, setContent] = useState(doc.summary || "");
  const [version, setVersion] = useState(doc.version || 1);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/pwr/activity/${doc.id}`, { summary: content, file_name: fileName });
      setVersion(data.version);
      toast.success(`Saved (v${data.version})`);
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const replaceFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5MB)"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await api.post(`/pwr/activity/${doc.id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setContent(data.summary);
      setFileName(data.file_name);
      setVersion(data.version);
      toast.success(`Replaced with "${file.name}" (v${data.version})`);
      onSaved && onSaved();
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="doc-editor-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative h-full w-full max-w-2xl bg-white shadow-2xl flex flex-col">
        <div className="bg-black px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white min-w-0">
            <FileText size={20} className="text-blue-400 shrink-0" />
            <input
              data-testid="doc-editor-filename"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              className="bg-transparent text-white font-bold text-sm w-full focus:outline-none border-b border-transparent focus:border-blue-400"
            />
            <span className="text-[10px] text-zinc-400 shrink-0">v{version}</span>
          </div>
          <button onClick={onClose} data-testid="doc-editor-close" className="text-white hover:text-red-400 ml-3"><X size={20} weight="bold" /></button>
        </div>
        <div className="px-5 py-2 text-xs text-zinc-500 border-b border-zinc-100 flex gap-4">
          <span>{doc.source_type}</span><span>·</span><span>{doc.project_name}</span>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <textarea
            data-testid="doc-editor-textarea"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Document content…"
            className="w-full h-[calc(100vh-260px)] min-h-[300px] bg-white border border-zinc-200 rounded-lg p-4 text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap focus:outline-none focus:border-blue-400 resize-none"
          />
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-zinc-200 bg-zinc-50">
          <input ref={fileRef} type="file" accept=".txt,.md,.json,.csv,.docx,.pdf" onChange={replaceFile} className="hidden" data-testid="doc-replace-input" />
          <button onClick={() => fileRef.current?.click()} disabled={saving} data-testid="doc-replace-btn" className="flex items-center gap-2 text-sm font-bold text-zinc-600 border border-zinc-300 rounded-full px-4 py-2 hover:bg-white transition-colors disabled:opacity-50">
            <Paperclip size={16} /> Replace with File
          </button>
          <button onClick={save} disabled={saving} data-testid="doc-editor-save" className="bg-orange-500 text-white font-bold rounded-full px-6 py-2 hover:bg-orange-600 transition-colors disabled:opacity-50">
            {saving ? "Saving…" : "Save Document"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- AI Profile Document (read-only ID Card) ---------- */
function ProfileModal({ collaborator, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/pwr/collaborators/${collaborator.id}/profile`).then((r) => setData(r.data)).catch(() => {}); }, [collaborator]);
  const c = data?.card?.content;
  return (
    <Overlay title="AI Profile Document" onClose={onClose} tint="stone">
      <div className="bg-stone-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <IdentificationBadge size={34} weight="fill" className="text-zinc-700" style={{ display: "none" }} />
          <img src={PWR("badge-white")} alt="" className="w-9 h-9 object-contain" style={{ filter: "brightness(0.2)" }} />
          <div>
            <p className="text-xs text-zinc-500 uppercase">Dig Moon · Collaborator ID</p>
            <h3 className="font-bold text-lg text-zinc-900">{collaborator.name}</h3>
          </div>
        </div>
        {!data ? <p className="text-sm text-zinc-500">Loading...</p> : !data.linked ? (
          <p className="text-sm text-zinc-600">No AI ID Card linked. Link one in App. Settings.</p>
        ) : (
          <div className="space-y-3 bg-white rounded-lg p-4 max-h-[50vh] overflow-y-auto">
            <p className="text-xs font-mono text-blue-600">{data.card.prid} · v{data.card.version}</p>
            {["project_context", "current_state", "next_steps", "responsibilities", "specialist_areas", "constraints", "key_decisions"].map((k) =>
              c?.[k] ? (<div key={k}><p className="text-xs uppercase font-bold text-zinc-500">{k.replace(/_/g, " ")}</p><p className="text-sm text-zinc-800 whitespace-pre-wrap">{c[k]}</p></div>) : null)}
            <p className="text-[10px] text-zinc-400 uppercase pt-2 border-t">Read-only · cannot be edited in PWR</p>
          </div>
        )}
      </div>
    </Overlay>
  );
}

/* ---------- Overlay shell ---------- */
function Overlay({ title, children, onClose, tint = "white", wide = false }) {
  return (
    <div className="absolute inset-0 top-[74px] flex items-start justify-center p-6 z-40">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className={`relative w-full ${wide ? "max-w-6xl" : "max-w-4xl"}`}>
        <div className="bg-black rounded-t-xl px-5 py-3 flex items-center justify-between">
          <h2 className="text-white font-bold">{title}</h2>
          <button onClick={onClose} data-testid="modal-close" className="text-white hover:text-red-400"><X size={20} weight="bold" /></button>
        </div>
        <div className={`rounded-b-xl p-4 ${tint === "blue" ? "bg-blue-200" : tint === "stone" ? "bg-stone-300" : "bg-zinc-100"} max-h-[calc(100vh-140px)] overflow-y-auto`}>
          {children}
        </div>
      </div>
    </div>
  );
}
